import { CommonModule } from "@angular/common";
import { NgModule, inject } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { MaintenanceGuard } from "@core/guards/maintenance.guard";
import { OnboardingRegisterGuard } from "@core/guards/onboarding-register.guard";
import { RoleGuard } from "@core/guards/role.guard";
import { RolesResolver } from "@core/helpers/roles.resolver";
import { AccessRoleEnum } from "@services/business/_models/roles";
import { MetaTagConfig } from "./types/meta-tag.interface";

import { PaymentRedirectService } from "./services/business/payments-services/payment-redirect.service";

const handlePaymentRedirect = (isSuccess: boolean) => () => {
  const redirectService = inject(PaymentRedirectService);
  redirectService.handlePaymentReturn(window.location.href, isSuccess);
  return false;
};

const handleRouterError = (error: unknown) => {
  if (error instanceof Error) {
    const message = error.message ?? "";
    if (
      message.includes("Importing a module script failed") ||
      message.includes("Loading chunk") ||
      message.includes("Failed to fetch dynamically imported module")
    ) {
      globalThis.location.reload();
      return;
    }
  }

  throw error;
};

const routes: Routes = [
  {
    path: "login",
    loadComponent: () =>
      import("./ui/pages/auth/login/login.component").then(
        m => m.LoginComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Login - Lawyerty",
        description:
          "Access your Lawyerty account. Secure login for legal professionals and clients.",
        keywords: "login, access, account, legal platform, authentication",
        robots: "index, follow",
      } as MetaTagConfig,
    },
  },
  {
    path: "callback",
    loadComponent: () =>
      import("./ui/pages/auth/callback/callback.component").then(
        m => m.CallbackComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Authentication - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "password-reset",
    loadComponent: () =>
      import("@ui/pages/auth/password-reset/password-reset.component").then(
        m => m.PasswordResetComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Password Reset - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "recover-pwd",
    loadComponent: () =>
      import("@ui/pages/auth/password-change/password-change.component").then(
        m => m.PasswordChangeComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Change Password - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "recover-pwd/:token",
    loadComponent: () =>
      import("@ui/pages/auth/password-change/password-change.component").then(
        m => m.PasswordChangeComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      meta: {
        title: "Change Password - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "profile",
    loadComponent: () =>
      import(
        "./ui/pages/my-profile/user-profile-page/user-profile-page.component"
      ).then(m => m.UserProfilePageComponent),
    canActivate: [MaintenanceGuard],
    resolve: {
      data: RolesResolver,
    },
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "My Profile - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "dashboard",
    loadChildren: () =>
      import("./ui/pages/dashboard-routing.module").then(
        m => m.DashboardRoutingModule
      ),
    resolve: {
      data: RolesResolver,
    },
    canActivate: [MaintenanceGuard, RoleGuard],
    data: {
      roles: [
        AccessRoleEnum.Lawyer,
        AccessRoleEnum.Admin,
        AccessRoleEnum.TenantAdmin,
        AccessRoleEnum.ChannelEmployee,
        AccessRoleEnum.ChannelAdmin,
      ],
      excludedRoles: [
        AccessRoleEnum.PlatformAdmin,
        AccessRoleEnum.PlatformSupport,
      ],
      meta: {
        title: "Dashboard - Lawyerty",
        description:
          "Manage your legal practice with Lawyerty's comprehensive dashboard. Access cases, clients, and reports.",
        keywords:
          "dashboard, legal management, cases, clients, reports, legal practice",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "initiate-payment/:paymentId",
    loadComponent: () =>
      import("@ui/pages/initiate-payment/initiate-payment.component").then(
        m => m.InitiatePaymentComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Payment - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "processPayment",
    canActivate: [MaintenanceGuard, handlePaymentRedirect(true)],
    children: [],
  },
  {
    path: "processPaymentFailed",
    canActivate: [MaintenanceGuard, handlePaymentRedirect(false)],
    children: [],
  },
  {
    path: "onboarding/:token",
    loadComponent: () =>
      import(
        "./ui/pages/auth/onboarding/onboarding-initial-page/onboarding-initial-page.component"
      ).then(m => m.OnboardingInitialPageComponent),
    canActivate: [MaintenanceGuard],
    data: {
      meta: {
        title: "Onboarding - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "onboarding/:token/pin",
    loadComponent: () =>
      import(
        "./ui/pages/auth/onboarding/onboarding-pin-page/onboarding-pin-page.component"
      ).then(m => m.OnboardingPinPageComponent),
    canActivate: [MaintenanceGuard],
    data: {
      meta: {
        title: "Onboarding - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "onboarding/:token/register",
    loadComponent: () =>
      import(
        "./ui/pages/auth/onboarding/onboarding-register-page/onboarding-register-page.component"
      ).then(m => m.OnboardingRegisterPageComponent),
    canActivate: [MaintenanceGuard, OnboardingRegisterGuard],
    data: {
      meta: {
        title: "Register - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "onboarding/:token/result",
    loadComponent: () =>
      import(
        "./ui/pages/auth/onboarding/onboarding-result-page/onboarding-result-page.component"
      ).then(m => m.OnboardingResultPageComponent),
    canActivate: [MaintenanceGuard],
    data: {
      meta: {
        title: "Onboarding Result - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "onboarding/:token/:pin",
    loadComponent: () =>
      import(
        "./ui/pages/auth/onboarding/onboarding-pin-page/onboarding-pin-page.component"
      ).then(m => m.OnboardingPinPageComponent),
    canActivate: [MaintenanceGuard],
    data: {
      meta: {
        title: "Onboarding - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "records",
    loadChildren: () =>
      import("./ui/pages/records/records-routing.module").then(
        m => m.PagesRoutingModule
      ),
    resolve: {
      data: RolesResolver,
    },
    canActivate: [MaintenanceGuard, RoleGuard],
    data: {
      excludedRoles: [
        AccessRoleEnum.Lawyer,
        AccessRoleEnum.Admin,
        AccessRoleEnum.TenantAdmin,
        AccessRoleEnum.PlatformAdmin,
        AccessRoleEnum.PlatformSupport,
        AccessRoleEnum.ChannelEmployee,
        AccessRoleEnum.ChannelAdmin,
      ],
      meta: {
        title: "Records - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "channel",
    loadChildren: () =>
      import("./ui/pages/channel-m2m/channel-m2m-routing.module").then(
        m => m.ChannelM2MRoutingModule
      ),
    resolve: {
      data: RolesResolver,
    },
    canActivate: [MaintenanceGuard, RoleGuard],
    data: {
      roles: [AccessRoleEnum.ChannelM2M],
      showExtraMenuHeader: false,
      showContactSidebar: false,
      meta: {
        title: "Records - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "supervision",
    loadChildren: () =>
      import("./ui/pages/supervision/supervision-routing.module").then(
        m => m.SupervisionRoutingModule
      ),
    resolve: {
      data: RolesResolver,
    },
    canActivate: [MaintenanceGuard, RoleGuard],
    data: {
      roles: [AccessRoleEnum.PlatformAdmin, AccessRoleEnum.PlatformSupport],
      meta: {
        title: "Supervision - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "messaging",
    loadComponent: () =>
      import("@ui/pages/messaging/messaging.component").then(
        m => m.MessagingComponent
      ),
    canActivate: [MaintenanceGuard],
    resolve: {
      data: RolesResolver,
    },
    data: {
      roles: [AccessRoleEnum.AuthenticatedUser],
      showMobileHeader: false,
      customMobileLayout: true,
      showContactSidebar: false,
      meta: {
        title: "Messaging - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "maintenance",
    loadComponent: () =>
      import("./ui/pages/maintenance/maintenance.component").then(
        m => m.MaintenanceComponent
      ),
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      showMobileMenu: false,
      meta: {
        title: "Maintenance - Lawyerty",
        description:
          "Lawyerty is currently under maintenance. We'll be back soon with improved services.",
        keywords: "maintenance, system update, temporary unavailable",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
  {
    path: "privacy-policy",
    loadComponent: () =>
      import("./ui/pages/privacy-policy/privacy-policy.component").then(
        m => m.PrivacyPolicyComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      showMobileMenu: false,
      meta: {
        title: "Privacy Policy - Lawyerty",
        description:
          "Read how Lawyerty handles your data: collection, use, sharing, security, and your rights.",
        keywords:
          "privacy policy, data protection, legal, security, rights, Lawyerty",
        robots: "index, follow",
      } as MetaTagConfig,
    },
  },
  {
    path: "faq",
    loadComponent: () =>
      import("./ui/pages/faq/faq.component").then(m => m.FaqComponent),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      showMobileMenu: false,
      meta: {
        title: "FAQ - Lawyerty",
        description:
          "Find answers to the most common questions about Lawyerty: access and registration, case files, documents, payments and more.",
        keywords:
          "faq, frequently asked questions, help, support, Lawyerty, legal platform",
        robots: "index, follow",
      } as MetaTagConfig,
    },
  },
  {
    path: "cookie-policy",
    loadComponent: () =>
      import("./ui/pages/cookie-policy/cookie-policy.component").then(
        m => m.CookiePolicyComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      showDesktopMenu: false,
      showExtraMenuHeader: false,
      showContactSidebar: false,
      showMobileMenu: false,
      meta: {
        title: "Cookie Policy - Lawyerty",
        description:
          "Learn how Lawyerty uses cookies, their purposes, and how to manage them.",
        keywords: "cookie policy, cookies, consent, Lawyerty, privacy",
        robots: "index, follow",
      } as MetaTagConfig,
    },
  },
  { path: "", redirectTo: "login", pathMatch: "full" },
  {
    path: "**",
    loadComponent: () =>
      import("./ui/components/not-found/not-found.component").then(
        m => m.NotFoundComponent
      ),
    canActivate: [MaintenanceGuard],
    data: {
      meta: {
        title: "Page Not Found - Lawyerty",
        robots: "noindex, nofollow",
      } as MetaTagConfig,
    },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: "always",
      errorHandler: handleRouterError,
    }),
    CommonModule,
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
